/**
 * Competitor & Industry Benchmarking Page
 * Compare company metrics against industry averages
 */
import { useState, useEffect } from 'react';
import { 
  BarChart3, TrendingUp, TrendingDown, Minus, Target, Users, DollarSign,
  Building2, Award, AlertTriangle, CheckCircle, Plus, Edit2, Trash2,
  RefreshCw, ChevronDown, ExternalLink, X, Save, Globe, Briefcase,
  ArrowUp, ArrowDown, Info, Sparkles, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INDUSTRIES = [
  { id: 'digital_marketing', name: 'Digital Marketing & Advertising', icon: '📢' },
  { id: 'consulting', name: 'Business Consulting', icon: '💼' },
  { id: 'tech_services', name: 'Technology Services', icon: '💻' },
  { id: 'ecommerce', name: 'E-Commerce & Retail', icon: '🛒' },
  { id: 'education', name: 'Education & Training', icon: '📚' }
];

const BenchmarkingPage = () => {
  const [selectedIndustry, setSelectedIndustry] = useState('digital_marketing');
  const [comparison, setComparison] = useState(null);
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCompetitor, setEditingCompetitor] = useState(null);
  const [formData, setFormData] = useState({
    name: '', industry: '', website: '', notes: '',
    estimated_revenue: '', estimated_staff: '',
    strengths: '', weaknesses: ''
  });

  useEffect(() => {
    fetchComparison();
    fetchCompetitors();
  }, [selectedIndustry]);

  const fetchComparison = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/benchmarking/compare/${selectedIndustry}`);
      if (res.ok) {
        const data = await res.json();
        setComparison(data);
      }
    } catch (err) {
      console.error('Error fetching comparison:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetitors = async () => {
    try {
      const res = await fetch(`${API_URL}/api/benchmarking/competitors`);
      if (res.ok) {
        const data = await res.json();
        setCompetitors(data.competitors || []);
      }
    } catch (err) {
      console.error('Error fetching competitors:', err);
    }
  };

  const handleSaveCompetitor = async () => {
    try {
      const payload = {
        ...formData,
        estimated_revenue: parseFloat(formData.estimated_revenue) || 0,
        estimated_staff: parseInt(formData.estimated_staff) || 0,
        strengths: formData.strengths.split(',').map(s => s.trim()).filter(s => s),
        weaknesses: formData.weaknesses.split(',').map(s => s.trim()).filter(s => s)
      };

      const url = editingCompetitor 
        ? `${API_URL}/api/benchmarking/competitors/${editingCompetitor.id}`
        : `${API_URL}/api/benchmarking/competitors`;
      
      const res = await fetch(url, {
        method: editingCompetitor ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setShowAddModal(false);
        setEditingCompetitor(null);
        resetForm();
        fetchCompetitors();
      }
    } catch (err) {
      console.error('Error saving competitor:', err);
    }
  };

  const handleDeleteCompetitor = async (id) => {
    if (!window.confirm('Padam pesaing ini?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/benchmarking/competitors/${id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        fetchCompetitors();
      }
    } catch (err) {
      console.error('Error deleting competitor:', err);
    }
  };

  const openEditModal = (competitor) => {
    setFormData({
      name: competitor.name || '',
      industry: competitor.industry || '',
      website: competitor.website || '',
      notes: competitor.notes || '',
      estimated_revenue: competitor.estimated_revenue?.toString() || '',
      estimated_staff: competitor.estimated_staff?.toString() || '',
      strengths: (competitor.strengths || []).join(', '),
      weaknesses: (competitor.weaknesses || []).join(', ')
    });
    setEditingCompetitor(competitor);
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '', industry: '', website: '', notes: '',
      estimated_revenue: '', estimated_staff: '',
      strengths: '', weaknesses: ''
    });
  };

  const formatCurrency = (value) => {
    return `RM ${Number(value || 0).toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'above': return 'text-emerald-600 bg-emerald-50';
      case 'below': return 'text-red-600 bg-red-50';
      default: return 'text-amber-600 bg-amber-50';
    }
  };

  const getStatusIcon = (status, betterIf) => {
    if (status === 'above') return <ArrowUp className="w-4 h-4 text-emerald-600" />;
    if (status === 'below') return <ArrowDown className="w-4 h-4 text-red-600" />;
    return <Minus className="w-4 h-4 text-amber-600" />;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  if (loading && !comparison) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="benchmarking-loading">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-3" />
          <p className="text-gray-600">Loading benchmarking data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="benchmarking-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900 via-indigo-800 to-purple-900 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-xl">
              <BarChart3 className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Industry Benchmarking</h1>
              <p className="text-indigo-200 text-sm mt-1">
                Bandingkan prestasi syarikat dengan purata industri
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <select
              value={selectedIndustry}
              onChange={(e) => setSelectedIndustry(e.target.value)}
              className="bg-white/10 border-white/20 border rounded-lg px-4 py-2 text-white text-sm min-w-[200px]"
              data-testid="industry-selector"
            >
              {INDUSTRIES.map(ind => (
                <option key={ind.id} value={ind.id} className="text-gray-800">
                  {ind.icon} {ind.name}
                </option>
              ))}
            </select>
            
            <button
              onClick={fetchComparison}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {comparison && (
        <>
          {/* Overall Score */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-1 bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">Overall Industry Score</p>
                <div className={`text-6xl font-bold ${getScoreColor(comparison.overall_score)}`}>
                  {comparison.overall_score}
                </div>
                <p className="text-gray-500 text-sm mt-2">/ 100</p>
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs text-gray-500">
                    {comparison.overall_score >= 80 ? '🏆 Above Industry Average' :
                     comparison.overall_score >= 60 ? '📊 On Par with Industry' :
                     '⚠️ Below Industry Average'}
                  </p>
                </div>
              </div>
            </div>

            {/* Company Summary */}
            <div className="md:col-span-2 bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-gray-600" />
                Your Company Summary (12 Months)
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-emerald-600">Total Revenue</p>
                  <p className="text-lg font-bold text-emerald-700">
                    {formatCurrency(comparison.company_summary.total_revenue)}
                  </p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-red-600">Total Expenses</p>
                  <p className="text-lg font-bold text-red-700">
                    {formatCurrency(comparison.company_summary.total_expenses)}
                  </p>
                </div>
                <div className={`${comparison.company_summary.profit >= 0 ? 'bg-blue-50' : 'bg-orange-50'} rounded-lg p-3 text-center`}>
                  <p className={`text-xs ${comparison.company_summary.profit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Net Profit</p>
                  <p className={`text-lg font-bold ${comparison.company_summary.profit >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                    {formatCurrency(comparison.company_summary.profit)}
                  </p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-purple-600">Staff Count</p>
                  <p className="text-lg font-bold text-purple-700">
                    {comparison.company_summary.staff_count}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Comparison */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100 bg-gray-50">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <Target className="w-5 h-5 text-indigo-600" />
                Metrics vs Industry: {comparison.industry.name}
              </h3>
            </div>
            
            <div className="divide-y divide-gray-100">
              {comparison.metrics.map((metric, i) => (
                <div key={i} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <p className="font-medium text-gray-800">{metric.name}</p>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(metric.status)}`}>
                          {metric.status === 'above' ? 'Above' : metric.status === 'below' ? 'Below' : 'On Par'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {metric.better_if === 'higher' ? '↑ Higher is better' : 
                         metric.better_if === 'lower' ? '↓ Lower is better' : '⚖️ Optimal range'}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-8">
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Your Company</p>
                        <p className="text-lg font-bold text-gray-800">
                          {typeof metric.company_value === 'number' && metric.company_value > 1000 
                            ? formatCurrency(metric.company_value)
                            : `${metric.company_value}${metric.unit === '%' || metric.unit === '% of revenue' ? '%' : ''}`}
                          {metric.unit === 'months' && ' bulan'}
                        </p>
                      </div>
                      
                      <div className="w-8 flex justify-center">
                        {getStatusIcon(metric.status, metric.better_if)}
                      </div>
                      
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Industry Avg</p>
                        <p className="text-lg font-bold text-indigo-600">
                          {typeof metric.industry_value === 'number' && metric.industry_value > 1000 
                            ? formatCurrency(metric.industry_value)
                            : `${metric.industry_value}${metric.unit === '%' || metric.unit === '% of revenue' ? '%' : ''}`}
                          {metric.unit === 'months' && ' bulan'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Visual Bar */}
                  <div className="mt-3 flex items-center gap-2">
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          metric.status === 'above' ? 'bg-emerald-500' :
                          metric.status === 'below' ? 'bg-red-500' : 'bg-amber-500'
                        }`}
                        style={{ 
                          width: `${Math.min(100, (metric.company_value / Math.max(metric.company_value, metric.industry_value)) * 100)}%` 
                        }}
                      />
                    </div>
                    <span className="text-xs text-gray-400 w-16 text-right">
                      {metric.company_value > metric.industry_value 
                        ? `+${((metric.company_value / metric.industry_value - 1) * 100).toFixed(0)}%`
                        : `-${((1 - metric.company_value / metric.industry_value) * 100).toFixed(0)}%`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Insights & Recommendations */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Insights */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  Key Insights
                </h3>
              </div>
              <div className="p-4 space-y-3">
                {comparison.insights.length > 0 ? comparison.insights.map((insight, i) => (
                  <div key={i} className={`p-3 rounded-lg border ${
                    insight.type === 'positive' ? 'bg-emerald-50 border-emerald-200' :
                    insight.type === 'critical' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                  }`}>
                    <div className="flex items-start gap-2">
                      {insight.type === 'positive' ? (
                        <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5" />
                      ) : (
                        <AlertTriangle className={`w-4 h-4 ${insight.type === 'critical' ? 'text-red-600' : 'text-amber-600'} mt-0.5`} />
                      )}
                      <div>
                        <p className={`font-medium text-sm ${
                          insight.type === 'positive' ? 'text-emerald-800' :
                          insight.type === 'critical' ? 'text-red-800' : 'text-amber-800'
                        }`}>{insight.title}</p>
                        <p className={`text-xs mt-1 ${
                          insight.type === 'positive' ? 'text-emerald-700' :
                          insight.type === 'critical' ? 'text-red-700' : 'text-amber-700'
                        }`}>{insight.message}</p>
                        <p className={`text-xs mt-2 font-medium ${
                          insight.type === 'positive' ? 'text-emerald-600' :
                          insight.type === 'critical' ? 'text-red-600' : 'text-amber-600'
                        }`}>💡 {insight.action}</p>
                      </div>
                    </div>
                  </div>
                )) : (
                  <p className="text-gray-500 text-sm text-center py-4">Tiada insights tersedia</p>
                )}
              </div>
            </div>

            {/* Recommendations */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-purple-500" />
                  Recommendations
                </h3>
              </div>
              <div className="p-4 space-y-3">
                {comparison.recommendations.map((rec, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {i + 1}
                    </div>
                    <p className="text-sm text-purple-800">{rec}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Competitors Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold text-gray-800 flex items-center gap-2">
            <Users className="w-5 h-5 text-gray-600" />
            Tracked Competitors
          </h3>
          <button
            onClick={() => { resetForm(); setEditingCompetitor(null); setShowAddModal(true); }}
            className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
            data-testid="add-competitor-btn"
          >
            <Plus className="w-4 h-4" />
            Add Competitor
          </button>
        </div>
        
        <div className="p-4">
          {competitors.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {competitors.map((competitor) => (
                <div key={competitor.id} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-bold text-gray-800">{competitor.name}</h4>
                      {competitor.industry && (
                        <p className="text-xs text-gray-500">{competitor.industry}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {competitor.website && (
                        <a href={competitor.website} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      <button onClick={() => openEditModal(competitor)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeleteCompetitor(competitor.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {(competitor.estimated_revenue > 0 || competitor.estimated_staff > 0) && (
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {competitor.estimated_revenue > 0 && (
                        <div className="bg-white rounded p-2 text-center">
                          <p className="text-xs text-gray-500">Est. Revenue</p>
                          <p className="text-sm font-medium text-gray-800">{formatCurrency(competitor.estimated_revenue)}</p>
                        </div>
                      )}
                      {competitor.estimated_staff > 0 && (
                        <div className="bg-white rounded p-2 text-center">
                          <p className="text-xs text-gray-500">Est. Staff</p>
                          <p className="text-sm font-medium text-gray-800">{competitor.estimated_staff}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {competitor.strengths?.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs text-emerald-600 font-medium mb-1">Strengths:</p>
                      <div className="flex flex-wrap gap-1">
                        {competitor.strengths.map((s, i) => (
                          <span key={i} className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {competitor.weaknesses?.length > 0 && (
                    <div>
                      <p className="text-xs text-red-600 font-medium mb-1">Weaknesses:</p>
                      <div className="flex flex-wrap gap-1">
                        {competitor.weaknesses.map((w, i) => (
                          <span key={i} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">{w}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {competitor.notes && (
                    <p className="text-xs text-gray-500 mt-2 italic">{competitor.notes}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Tiada pesaing direkodkan</p>
              <p className="text-sm text-gray-400 mt-1">Klik &quot;Add Competitor&quot; untuk mula track pesaing</p>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Competitor Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto" data-testid="competitor-modal">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-bold text-gray-800">
                {editingCompetitor ? 'Edit Competitor' : 'Add Competitor'}
              </h3>
              <button onClick={() => { setShowAddModal(false); setEditingCompetitor(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nama Syarikat *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2"
                  placeholder="Nama pesaing"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                  <input
                    type="text"
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2"
                    placeholder="e.g. Digital Marketing"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2"
                    placeholder="https://..."
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Est. Revenue (RM/year)</label>
                  <input
                    type="number"
                    value={formData.estimated_revenue}
                    onChange={(e) => setFormData({ ...formData, estimated_revenue: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Est. Staff Count</label>
                  <input
                    type="number"
                    value={formData.estimated_staff}
                    onChange={(e) => setFormData({ ...formData, estimated_staff: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2"
                    placeholder="0"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Strengths (comma separated)</label>
                <input
                  type="text"
                  value={formData.strengths}
                  onChange={(e) => setFormData({ ...formData, strengths: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2"
                  placeholder="e.g. Brand recognition, Large team, Good pricing"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weaknesses (comma separated)</label>
                <input
                  type="text"
                  value={formData.weaknesses}
                  onChange={(e) => setFormData({ ...formData, weaknesses: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2"
                  placeholder="e.g. Slow response, Limited services"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 h-20"
                  placeholder="Additional notes..."
                />
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => { setShowAddModal(false); setEditingCompetitor(null); }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Batal
              </button>
              <button
                onClick={handleSaveCompetitor}
                disabled={!formData.name}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Simpan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BenchmarkingPage;
